import { apiRequest, ApiRequestError, apiOrigin } from '../lib/api-client';

/** The media library (context.md sections 60-65). */

export type FileVisibility = 'PRIVATE' | 'TENANT' | 'PUBLIC';

export interface FileView {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  /** Whether the person asking has starred it (Phase 145). */
  favourite?: boolean;
  status: string;
  visibility: FileVisibility;
  folderId: string | null;
  tags: string[];
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  ownerId: string | null;
  createdAt: string;
}

export interface FolderRow {
  id: string;
  name: string;
  parent_id: string | null;
  /** How many files sit directly in it. */
  file_count?: number;
  /**
   * Non-null marks a folder the product maintains — where conversation media is filed.
   *
   * Only sent to somebody who may see it: for everybody else these folders are not in the list at
   * all, and neither are their files.
   */
  system_key: string | null;
}

export interface StorageUsage {
  usedBytes: number;
  fileCount: number;
  quotaBytes: number | null;
  remainingBytes: number | null;
}

export interface AllowedType {
  mime: string;
  extensions: string[];
  category: string;
}

export interface FileFilters {
  search?: string;
  folderId?: string;
  root?: boolean;
  tags?: string[];
  mimePrefix?: string;
  cursor?: string;
  /** Which shelf the sidebar is on (Phase 145). */
  shelf?: Shelf;
}

export function listFiles(
  filters: FileFilters = {},
): Promise<{ files: FileView[]; nextCursor: string | null }> {
  const params = new URLSearchParams({ limit: '60' });

  if (filters.search) params.set('search', filters.search);
  if (filters.folderId) params.set('folderId', filters.folderId);
  if (filters.root) params.set('root', 'true');
  if (filters.tags && filters.tags.length > 0) params.set('tags', filters.tags.join(','));
  if (filters.mimePrefix) params.set('mimePrefix', filters.mimePrefix);
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.shelf && filters.shelf !== 'ALL') params.set('shelf', filters.shelf);

  return apiRequest(`/v1/files?${params.toString()}`);
}

export type Shelf = 'ALL' | 'RECENT' | 'SHARED' | 'FAVOURITES' | 'TRASH';

export interface Library {
  folders: FolderRow[];
  counts: { all: number; recent: number; shared: number; favourites: number; trashed: number };
  usage: StorageUsage;
}

/**
 * The sidebar, in one call (Phase 145).
 *
 * Four counts, the tree and the quota describe one moment; four round trips would let them
 * describe four.
 */
export function getLibrary(): Promise<Library> {
  return apiRequest('/v1/files/library');
}

export function restoreFile(id: string): Promise<FileView> {
  return apiRequest(`/v1/files/${id}/restore`, { method: 'POST' });
}

export function setFavourite(id: string, wanted: boolean): Promise<void> {
  return apiRequest(`/v1/files/${id}/favourite`, { method: wanted ? 'PUT' : 'DELETE' });
}

export function listFolders(): Promise<{ folders: FolderRow[] }> {
  return apiRequest('/v1/files/folders');
}

export function createFolder(input: {
  name: string;
  parentId?: string | null;
}): Promise<FolderRow> {
  return apiRequest('/v1/files/folders', { method: 'POST', body: input });
}

export function deleteFolder(id: string): Promise<void> {
  return apiRequest(`/v1/files/folders/${id}`, { method: 'DELETE' });
}

export function storageUsage(): Promise<StorageUsage> {
  return apiRequest('/v1/files/usage');
}

export function allowedTypes(): Promise<{ maxUploadBytes: number; types: AllowedType[] }> {
  return apiRequest('/v1/files/types');
}

export function updateFile(
  id: string,
  patch: {
    name?: string;
    tags?: string[];
    visibility?: FileVisibility;
    folderId?: string | null;
  },
): Promise<FileView> {
  return apiRequest(`/v1/files/${id}`, { method: 'PATCH', body: patch });
}

export function deleteFile(id: string): Promise<void> {
  return apiRequest(`/v1/files/${id}`, { method: 'DELETE' });
}

/**
 * A short-lived signed URL for one file.
 *
 * Fetched on demand rather than listed with the rows: a download link is a credential with a clock
 * on it, and a list that handed out fifty of them would be handing out fifty credentials nobody
 * asked for (context.md sections 63-64).
 */
export function downloadUrl(
  id: string,
  asAttachment = false,
): Promise<{ url: string; inline: boolean; mimeType: string }> {
  return apiRequest(`/v1/files/${id}/download?attachment=${asAttachment}`);
}

interface UploadTicket {
  file: FileView;
  upload: { url: string; method: string; headers: Record<string, string>; expiresAt: string };
}

/**
 * Sends one file, the way production sends it.
 *
 * Three calls, not one: the API reserves the row and issues a signed URL, the bytes go straight to
 * the object store, and completion is what checks the magic bytes against the declared type. The
 * browser never posts the file to our API, so a large upload never has to fit in a request body,
 * and the same code path works against R2 and against the local development provider
 * (docs/STORAGE.md).
 */
export async function uploadFile(
  file: File,
  options: { folderId?: string | null; tags?: string[]; visibility?: FileVisibility } = {},
): Promise<FileView> {
  const ticket = await apiRequest<UploadTicket>('/v1/files/uploads', {
    method: 'POST',
    body: {
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      resourceType: 'media',
      ...(options.folderId ? { folderId: options.folderId } : {}),
      ...(options.tags && options.tags.length > 0 ? { tags: options.tags } : {}),
      ...(options.visibility ? { visibility: options.visibility } : {}),
    },
  });

  await sendBytes(ticket, file);

  return apiRequest<FileView>(`/v1/files/uploads/${ticket.file.id}/complete`, { method: 'POST' });
}

/**
 * The bytes, by the direct route if the object store will take them and through the API if it will
 * not.
 *
 * A bucket that has not been given a CORS policy does not refuse the upload with a status — it
 * refuses the request before it is made, and all a page sees is `TypeError: Failed to fetch`, the
 * same thing it would see offline. That is a deployment step nobody should have to have completed
 * before the product works, so the relay stands behind it. A real rejection, one that came back with
 * a status, is not retried: the storage answered, and it said no.
 */
/**
 * Whether the direct route has already proved unreachable from this page.
 *
 * A bucket with no CORS policy refuses the request before it is made, so every upload pays a failed
 * round trip and writes a red CORS error into the console — which reads, to anybody looking, like
 * the upload failing. It did not: the relay behind it carried the bytes. Remembering the first
 * refusal for the life of the tab spares the rest of them.
 *
 * Deliberately not persisted. A CORS policy added on Tuesday should take effect on Wednesday
 * without anybody clearing storage.
 */
let directUploadBlocked = false;

async function sendBytes(ticket: UploadTicket, file: File): Promise<void> {
  if (directUploadBlocked) {
    await relayBytes(ticket, file);

    return;
  }

  try {
    const response = await fetch(absolute(ticket.upload.url), {
      method: ticket.upload.method,
      headers: ticket.upload.headers,
      body: file,
    });

    if (response.ok) return;

    throw new ApiRequestError(
      response.status,
      'storage.upload_refused',
      'O armazenamento recusou o envio deste arquivo.',
      null,
    );
  } catch (caught) {
    if (caught instanceof ApiRequestError) throw caught;

    // Refused before it was sent: CORS, or no network at all. The relay knows which.
    directUploadBlocked = true;

    await relayBytes(ticket, file);
  }
}

/** The same bytes, through the API, which has no cross-origin question to answer. */
async function relayBytes(ticket: UploadTicket, file: File): Promise<void> {
  await apiRequest(`/v1/files/uploads/${ticket.file.id}/bytes`, {
    method: 'POST',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    rawBody: file,
  });
}

/**
 * The signed URL is absolute in production and may be relative in development; either way it is the
 * API's own origin that fills in the gap, never the page's.
 */
function absolute(url: string): string {
  return url.startsWith('http') ? url : `${apiOrigin()}${url}`;
}

// ---------------------------------------------------------------------------
// Share links (Phase 138)
// ---------------------------------------------------------------------------

export interface ShareView {
  id: string;
  subject: 'FILE' | 'FOLDER';
  fileId: string | null;
  folderId: string | null;
  name: string;
  /**
   * Only ever filled on the response that created it.
   *
   * The server keeps the token's hash and not the token, so a link somebody has lost is revoked
   * and made again rather than looked up — which is also what makes a copy of the database not a
   * copy of every live link.
   */
  url: string;
  hasPassword: boolean;
  expiresAt: string | null;
  maxViews: number | null;
  maxDownloads: number | null;
  views: number;
  downloads: number;
  remainingViews: number | null;
  remainingDownloads: number | null;
  revokedAt: string | null;
  note: string | null;
  createdAt: string;
}

export interface CreateShareInput {
  fileId?: string | null;
  folderId?: string | null;
  expiresAt?: string | null;
  maxViews?: number | null;
  maxDownloads?: number | null;
  password?: string | null;
  note?: string | null;
}

export function listShares(): Promise<{ shares: ShareView[] }> {
  return apiRequest('/v1/files/shares');
}

export function createShare(input: CreateShareInput): Promise<ShareView> {
  return apiRequest('/v1/files/shares', { method: 'POST', body: input });
}

export function revokeShare(id: string): Promise<void> {
  return apiRequest(`/v1/files/shares/${id}/revoke`, { method: 'POST' });
}

export function deleteShare(id: string): Promise<void> {
  return apiRequest(`/v1/files/shares/${id}`, { method: 'DELETE' });
}

// ----- the guest's side, which has no session ------------------------------

export interface PublicShareView {
  subject: 'FILE' | 'FOLDER';
  name: string;
  needsPassword: boolean;
  expiresAt: string | null;
  remainingDownloads: number | null;
  files: { id: string; name: string; mimeType: string; sizeBytes: number }[];
}

export function peekShare(token: string): Promise<{ needsPassword: boolean; name: string }> {
  return apiRequest(`/pub/shares/${encodeURIComponent(token)}`);
}

/** Where the browser fetches the bytes: a POST, because a download spends one of the allowance. */
export function sharedFileUrl(token: string): string {
  return `/pub/shares/${encodeURIComponent(token)}/files`;
}

export function openShare(token: string, password?: string | null): Promise<PublicShareView> {
  return apiRequest(`/pub/shares/${encodeURIComponent(token)}/open`, {
    method: 'POST',
    body: password ? { password } : {},
  });
}
